import { useParams } from 'react-router'
import { useEffect, useState } from 'react'

import { type OnboardingPage } from '../routes'
import { useOnboardingWorkflow } from '../workflow'
import { OnboardingTitle } from '../components/OnboardingTitle'
import { OnboardingNextButton } from '../components/OnboardingNextButton'
import { PricingRCPCoversSection } from '../components/PricingRCPCoversSection'
import { useQuotation } from '../context'
import {
  DealCreateInput,
  ProductEnum,
  type QuotationAddress,
  useCreateDealActiveCampaignQuery,
} from '../../generated/graphql'
import { PricingEcommCoversSection } from '../components/PricingEcommCoversSection'
import { PricingMRPCoversSection } from '../components/PricingMRPCoversSection'
import { TipsCard } from '../components/TipsCard'
import { SummaryStartDate } from '../components/summary/SummaryStartDate'
import { SummaryPaymentFrequency } from '../components/summary/SummaryPaymentFrequency'
import { SummaryEmail } from '../components/summary/SummaryEmail'
import { SummaryFirstName } from '../components/summary/SummaryFirstName'
import { SummaryLastName } from '../components/summary/SummaryLastName'
import { SummaryPhone } from '../components/summary/SummaryPhone'
import { track } from '../../utils/analytics'
import { SummaryFormuleChoisie } from '../components/summary/SummaryFormuleChoisie'
import { PricingASDCoversSection } from '../components/PricingASDCoversSection'
import { PricingMREAcheelCoversSection } from '../components/PricingMREAcheelCoversSection'

const getInlineAddress = (address?: QuotationAddress) => {
  return `${address?.line1 ?? ''}${address?.line2 ? `,${address.line2} ` : ''}, ${
    address?.postalCode ?? ''
  }, ${address?.city ?? ''}`
}

const Main = () => {
  const { id } = useParams()
  const { proceed } = useOnboardingWorkflow()
  const { quotation, updateQuotation } = useQuotation()
  const [search, setSearch] = useState(false)

  let quotChem
  if (quotation.details.products?.includes(ProductEnum.MreAcheel)) {
    quotChem = quotation.premium?.mreAcheel
  }

  const deal: DealCreateInput = {
    quotation: {
      number: quotation.number,
      listName: 'OC - Professionnels Eco',
      pipelineTitle: 'Pro Eco',
      id: quotation.id,
      originalTotal: quotChem?.originalTotal ? quotChem.originalTotal : 0,
      discountedTotal: quotChem?.discountedTotal,
      base: quotChem?.base,
      status: quotation.status,
      quotation: {
        activeCampaignId: quotation.details.activeCampaignId,
        activeCampaignDealId: quotation.details.activeCampaignDealId,
        startDate: quotation.details.startDate, //nouvelle info
        contact: {
          email: quotation.details.contact?.email,
          phone: quotation.details.contact?.phone,
          firstName: quotation.details.contact?.firstName,
          lastName: quotation.details.contact?.lastName,
          marketingOptIn: quotation.details.contact?.marketingOptIn,
          userTypeName: quotation.details.contact?.userTypeName,
        },
        products: quotation.details.products,
        turnover: quotation.details.turnover,
        billingAddress: {
          city: quotation.details.billingAddress?.city,
          line1: quotation.details.billingAddress?.line1,
          line2: quotation.details.billingAddress?.line2,
          country: quotation.details.billingAddress?.country,
          postalCode: quotation.details.billingAddress?.postalCode,
        },
        company: {
          name: quotation.details.company?.name,
          nafCode: quotation.details.company?.nafCode,
          siret: quotation.details.company?.siret,
          legalStatus: quotation.details.company?.legalStatus,
          foundedAt: quotation.details.company?.foundedAt,
          address: {
            city: quotation.details.company?.address?.city,
            line1: quotation.details.company?.address?.line1,
            line2: quotation.details.company?.address?.line2,
            postalCode: quotation.details.company?.address?.postalCode,
            country: quotation.details.company?.address?.country,
          },
        },
        mreAcheel: {
          activityId: quotation.details.mreAcheel?.activityId,
          packageOptionMAcheel: quotation.details.mreAcheel?.packageOptionMAcheel,
          surfaceArea: quotation.details.mreAcheel?.surfaceArea,
          // assistanceOption: quotation.details.mreAcheel?.assistanceOption,
          nbSinistres: quotation.details.mreAcheel?.nbSinistres,
          deductibleAmount: quotation.details.mreAcheel?.deductibleAmount,
          extincteursLocaux: quotation.details.mreAcheel?.extincteursLocaux,
          historicalMonument: quotation.details.mreAcheel?.historicalMonument,
          resilieNonPaiement: quotation.details.mreAcheel?.resilieNonPaiement,
          procedureCollective: quotation.details.mreAcheel?.procedureCollective,
          installElectriqueVerif: quotation.details.mreAcheel?.installElectriqueVerif,

          // assistanceOption: quotation.details.mreAcheel?.assistanceOption,
          // legalProtectionOption: quotation.details.mreAcheel?.legalProtectionOption,
          // operatingLiabilityOption: quotation.details.mreAcheel?.operatingLiabilityOption,
          // financialLossStandardOption: quotation.details.mreAcheel?.financialLossStandardOption,
          // financialLossAdvantageOption: quotation.details.mreAcheel?.financialLossAdvantageOption,
        },
        paymentFrequency: quotation?.details.paymentFrequency,
      },
    },
  }

  if (
    quotation.details.products?.includes(ProductEnum.MreAcheel) &&
    quotation.details.mreAcheel?.procedureCollective == false
  ) {
    const { data: results, isLoading } = useCreateDealActiveCampaignQuery(
      { query: deal },
      { enabled: search }
    )
    if (!isLoading) {
      proceed(() =>
        updateQuotation({
          details: {
            activeCampaignId: results?.createDealActiveCampaign?.activeCampaignId,
            activeCampaignDealId: results?.createDealActiveCampaign?.activeCampaignDealId,
          },
        })
      )
    }
  } else {
    proceed()
  }

  const onSubmit = () => {
    track({
      event: 'submitted_summary',
      quotation_id: id,
    })
    setSearch(true)
  }

  useEffect(() => {
    track({
      event: 'visited_summary',
      quotation_id: id,
    })
  }, [id])

  return (
    <>
      <OnboardingTitle.H1>Récapitulatif de vos données contractuelles</OnboardingTitle.H1>

      <div className="space-y-8">
        <div>
          <OnboardingTitle.H2>Coordonnées du signataire</OnboardingTitle.H2>
          <div className="space-y-3">
            <SummaryEmail />
            <SummaryFirstName />
            <SummaryLastName />
            <SummaryPhone />
          </div>
        </div>

        <div>
          <OnboardingTitle.H2>A propos de votre entreprise</OnboardingTitle.H2>
          <div className="space-y-3">
            <div>
              <span className="font-medium">Nom:</span> {quotation.details.company?.name}
            </div>
            <div>
              <span className="font-medium">Siret:</span> {quotation.details.company?.siret}
            </div>
            <div>
              <span className="font-medium">Code Naf:</span> {quotation.details.company?.nafCode}
            </div>
            <div>
              <span className="font-medium">Statut:</span> {quotation.details.company?.legalStatus}
            </div>
            <div>
              <span className="font-medium">Date de création:</span>{' '}
              {quotation.details.company?.foundedAt}
            </div>
            <div>
              <span className="font-medium">Chiffre d'affaire:</span> {quotation.details.turnover}
            </div>
            <div>
              <span className="font-medium">Siège social:</span>{' '}
              {getInlineAddress(quotation.details.company?.address ?? undefined)}
            </div>
            <div>
              <span className="font-medium">Adresse de facturation:</span>{' '}
              {getInlineAddress(
                quotation.details.billingAddress ?? quotation.details.company?.address ?? undefined
              )}
            </div>
          </div>
        </div>

        <div>
          <OnboardingTitle.H2>Votre produit d'assurance</OnboardingTitle.H2>
          <div className="space-y-3">
            <SummaryStartDate />
            <SummaryPaymentFrequency />
            <SummaryFormuleChoisie />
          </div>
        </div>

        {quotation.details.products?.length && (
          <div>
            {quotation.details.products?.some((p) => p === ProductEnum.PublicLiability) && (
              <PricingRCPCoversSection extended displayOnlyCover />
            )}
            {quotation.details.products?.some((p) => p === ProductEnum.PublicLiabilityEcomm) && (
              <PricingEcommCoversSection extended displayOnlyCover />
            )}
            {quotation.details.products?.some((p) => p === ProductEnum.MultiRisk) && (
              <PricingMRPCoversSection extended displayOnlyCover />
            )}
            {quotation.details.products?.some((p) => p === ProductEnum.MreAcheel) && (
              <PricingMREAcheelCoversSection extended displayOnlyCover />
            )}
            {quotation.details.products?.some(
              (p) => p === ProductEnum.AssistanceDomestiqueProfessionnel
            ) && <PricingASDCoversSection extended displayOnlyCover />}
            {quotation.details.products?.some((p) => p === ProductEnum.AssistanceDomestique) && (
              <PricingASDCoversSection extended displayOnlyCover />
            )}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <OnboardingNextButton onClick={() => onSubmit()} />
        </div>
      </div>
    </>
  )
}

const Tips = () => {
  return (
    <div className="space-y-3">
      <TipsCard
        title="Pourquoi dois-je valider des informations que j’ai déjà saisies ?"
        content={[
          'Ces informations seront celles qui vont apparaitre sur votre contrat et elles sont engageantes. N’hésitez pas à contacter un de nos experts si vous avez la moindre question ou doute.',
        ]}
      />
    </div>
  )
}

export default {
  main: <Main />,
  tips: <Tips />,
} satisfies OnboardingPage
